import { create } from "zustand"
import {
  loadPersistedThreadRuntimeViewedEntries,
  persistThreadRuntimeViewedEntry,
} from "@/lib/thread-runtime-viewed-state-storage"

export type RuntimeState = "idle" | "running" | "error"
export type ThreadDisplayState = "idle" | "running" | "waiting" | "error" | "suspended"
export type FinishReason =
  | "completed"
  | "error"
  | "aborted"
  | "retry_exhausted"
  | "waiting_input"
  | null

export type YoloFlowRuntimeStep = "idle" | "quick_pr" | "merge_pr" | "archiving" | "done" | "error"
export type YoloMergeFailureKind = "conflicting" | "behind" | "blocked" | "draft" | "unknown"
export type PrFlowRuntimeKind = "none" | "manual_create_pr" | "quick_pr" | "update_pr" | "merge_pr" | "archive_thread" | "yolo"
export type PrFlowRuntimeStatus = "idle" | "accepted" | "running" | "succeeded" | "failed" | "cancelled" | "reconciled"
export type PrFlowRuntimeStep =
  | "idle"
  | "validating"
  | "queued"
  | "resuming_sandbox"
  | "awaiting_draft"
  | "awaiting_finalize"
  | "staging"
  | "committing"
  | "pushing"
  | "creating_pr"
  | "linking_existing_pr"
  | "success"
  | "failure"
export type KnownSandboxStatus =
  | "none"
  | "creating"
  | "starting_services"
  | "starting_agent"
  | "starting_opencode"
  | "connecting_sse"
  | "running"
  | "stopped"
  | "error"

export interface ThreadRuntimeRecoveryStatus {
  level: "warning" | "blocked"
  label: string
  detail: string | null
}

export interface ThreadRuntimeEntry {
  sessionId: string
  state: RuntimeState
  lastActivityEventId: number | null
  lastFinishedEventId: number | null
  lastFinishedAt: number | null
  lastFinishReason: FinishReason
  rawTailId: number | null
  terminalRawCursor: number | null
  terminalRawCursorObservedAt: number | null
  lastViewedEventId: number
  /**
   * Server-emitted status snapshot freshness token.
   *
   * This must only contain timestamps observed inside SessionDO status payloads.
   * Mixing in browser-local `Date.now()` values would compare different clocks
   * and can let stale direct-status snapshots override newer runtime state.
   */
  activityStateObservedAt: number | null
  /**
   * Freshness token for mirrored lifecycle / coarse persisted session status.
   * This intentionally stays separate from `activityStateObservedAt` so session
   * row refetches cannot accidentally outrank fresher runtime activity metadata.
   */
  lifecycleStatusObservedAt: number | null
  /**
   * Freshness token for mirrored PR visual state.
   * PR state has its own fanout and polling repair paths, so it should not share
   * a clock domain with runtime status snapshots or lifecycle mirrors.
   */
  prStateObservedAt: number | null
  /**
   * Freshness token for mirrored PR mergeability state. Mergeability can be
   * discovered after an explicit merge attempt, so keep it separate from the
   * lifecycle-ish PR state while still allowing the sidebar to show conflicts.
   */
  prMergeabilityObservedAt: number | null
  lastKnownPersistedStatus: string | null
  lastKnownLifecycleStatus: string | null
  lastKnownPrNumber: number | null
  lastKnownPrUrl: string | null
  lastKnownPrState: string | null
  lastKnownPrMergeable: boolean | null
  lastKnownPrMergeableState: string | null
  lastKnownPrMergeabilityKnown: boolean
  lastKnownYoloMergeFailureKind: YoloMergeFailureKind | null
  yoloMergeFailureObservedAt: number | null
  optimisticWorkRequestPending: boolean
  pendingMessageCount: number
  latestPendingWorkTimestamp: number | null
  optimisticYoloStartPending: boolean
  yoloFlowStep: YoloFlowRuntimeStep
  yoloFlowUpdatedAt: number | null
  prFlowOperationKind: PrFlowRuntimeKind
  prFlowOperationStatus: PrFlowRuntimeStatus
  prFlowOperationStep: PrFlowRuntimeStep
  prFlowOperationUpdatedAt: number | null
  lastKnownSandboxStatus: KnownSandboxStatus | null
  recoveryStatusObservedAt: number | null
  lastKnownRecoveryStatus: ThreadRuntimeRecoveryStatus | null
}

interface SetOptimisticWorkRequestPendingInput {
  sessionId: string
  pending: boolean
  timeoutMs?: number | null
}

interface SetPendingMessageCountInput {
  sessionId: string
  pendingMessageCount: number
  latestPendingWorkTimestamp?: number | null
}

interface SetOptimisticYoloStartPendingInput {
  sessionId: string
  pending: boolean
  timeoutMs?: number | null
}

interface SetYoloFlowStepInput {
  sessionId: string
  step: YoloFlowRuntimeStep
  timestamp?: number | null
}

interface SetPrFlowOperationInput {
  sessionId: string
  kind: PrFlowRuntimeKind
  status: PrFlowRuntimeStatus
  step?: PrFlowRuntimeStep
  timestamp?: number | null
}

export interface PersistedThreadRuntimeSnapshotInput {
  sessionId: string
  state?: RuntimeState | null
  lastActivityEventId?: number | null
  lastFinishedEventId?: number | null
  lastFinishedAt?: number | null
  lastFinishReason?: FinishReason
  rawTailId?: number | null
  terminalRawCursor?: number | null
  terminalRawCursorObservedAt?: number | null
  lastViewedEventId?: number
}

interface ApplyThreadActivityInput {
  sessionId: string
  state: RuntimeState
  eventId: number
  finishedEventId?: number | null
  finishedAt?: number | null
  finishReason?: string | null
  rawTailId?: number | null
  terminalRawCursor?: number | null
  terminalRawCursorObservedAt?: number | null
}

interface ApplyThreadStatusInput {
  sessionId: string
  activityState?: string | null
  lastActivityEventId?: number | null
  lastFinishedEventId?: number | null
  lastFinishedAt?: number | null
  lastFinishReason?: string | null
  rawTailId?: number | null
  terminalRawCursor?: number | null
  terminalRawCursorObservedAt?: number | null
  sandboxStatus?: string | null
  persistedStatus?: string | null
  lifecycleStatus?: string | null
  observedAt?: number | null
}

interface ApplyThreadStatusesResult {
  appliedCount: number
}

interface ComputeThreadStatusEntryResult {
  result: ApplyThreadStatusResult
  next: ThreadRuntimeEntry | null
  shouldClearOptimisticWorkRequest: boolean
}

export interface ApplyThreadStatusResult {
  snapshotAccepted: boolean
  activityAccepted: boolean
  sandboxAccepted: boolean
  lifecycleAccepted: boolean
}

interface ApplyThreadPrStateInput {
  sessionId: string
  prState?: string | null
  mergeableState?: string | null
  mergeabilityKnown?: boolean
  observedAt?: number | null
}

export interface ApplyThreadPrStatusInput {
  sessionId: string
  prNumber?: number | null
  prUrl?: string | null
  prState?: string | null
  mergeable?: boolean | null
  mergeableState?: string | null
  mergeabilityKnown?: boolean
  observedAt?: number | null
}

interface ApplyThreadPrMergeFailureHintInput {
  sessionId: string
  mergeFailureKind?: YoloMergeFailureKind | null
  observedAt?: number | null
}

interface ApplyThreadRecoveryStatusInput {
  sessionId: string
  recoveryStatus?: ThreadRuntimeRecoveryStatus | null
  observedAt?: number | null
}

interface SetPrFlowOperationsResult {
  appliedCount: number
  appliedInputs: SetPrFlowOperationInput[]
}

interface ThreadRuntimeStore {
  entries: Record<string, ThreadRuntimeEntry>
  backgroundWarmingEntriesRevision: number
  notificationEntriesRevision: number
  faviconEntriesRevision: number
  /**
   * Viewer-local read cursors must survive runtime-entry pruning during
   * multiplex subscription churn, so keep them separate from `entries`.
   */
  viewedEventIds: Record<string, number>
  applyThreadActivity: (input: ApplyThreadActivityInput) => void
  applyThreadActivities: (inputs: ApplyThreadActivityInput[]) => number
  applyThreadStatus: (input: ApplyThreadStatusInput) => ApplyThreadStatusResult
  applyThreadStatuses: (inputs: ApplyThreadStatusInput[]) => ApplyThreadStatusesResult
  applyThreadPrState: (input: ApplyThreadPrStateInput) => boolean
  applyThreadPrStates: (inputs: ApplyThreadPrStateInput[]) => number
  applyThreadPrStatus: (input: ApplyThreadPrStatusInput) => boolean
  applyThreadPrStatuses: (inputs: ApplyThreadPrStatusInput[]) => number
  applyThreadPrMergeFailureHint: (input: ApplyThreadPrMergeFailureHintInput) => boolean
  applyThreadRecoveryStatus: (input: ApplyThreadRecoveryStatusInput) => boolean
  applyThreadRecoveryStatuses: (inputs: ApplyThreadRecoveryStatusInput[]) => number
  applyPersistedEntries: (entries: PersistedThreadRuntimeSnapshotInput[]) => void
  setOptimisticWorkRequestPending: (input: SetOptimisticWorkRequestPendingInput) => void
  setPendingMessageCount: (input: SetPendingMessageCountInput) => void
  setOptimisticYoloStartPending: (input: SetOptimisticYoloStartPendingInput) => void
  setYoloFlowStep: (input: SetYoloFlowStepInput) => void
  setPrFlowOperation: (input: SetPrFlowOperationInput) => boolean
  setPrFlowOperations: (inputs: SetPrFlowOperationInput[]) => SetPrFlowOperationsResult
  markViewed: (sessionId: string) => number | null
  markViewedByEventId: (sessionId: string, viewedEventId: number) => number | null
  pruneToSessions: (sessionIds: string[]) => void
  getLastActivityEventId: (sessionId: string) => number | null
}

let threadRuntimeViewedStatePersistenceScopeKey: string | null = null

export function configureThreadRuntimeViewedStatePersistence(scopeKey: string | null): void {
  const normalizedScopeKey = typeof scopeKey === "string" && scopeKey.trim().length > 0
    ? scopeKey.trim()
    : null

  if (threadRuntimeViewedStatePersistenceScopeKey === normalizedScopeKey) {
    return
  }

  threadRuntimeViewedStatePersistenceScopeKey = normalizedScopeKey

  if (Object.keys(useThreadRuntime.getState().viewedEventIds).length > 0) {
    useThreadRuntime.setState({ viewedEventIds: {} })
  }

  if (normalizedScopeKey !== null) {
    hydrateThreadRuntimeViewedState(normalizedScopeKey)
  }
}

export function hydrateThreadRuntimeViewedState(scopeKey: string | null): void {
  if (typeof scopeKey !== "string" || scopeKey.trim().length === 0) {
    return
  }

  useThreadRuntime.getState().applyPersistedEntries(
    loadPersistedThreadRuntimeViewedEntries({ scopeKey }),
  )
}

function normalizeEventId(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  return Math.floor(value)
}

function normalizeTimestamp(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  return Math.floor(value)
}

function normalizeRuntimeState(value: unknown): RuntimeState {
  if (value === "running") return "running"
  if (value === "error") return "error"
  return "idle"
}

function normalizeFinishReason(value: unknown): FinishReason {
  if (
    value === "completed" ||
    value === "error" ||
    value === "aborted" ||
    value === "retry_exhausted" ||
    value === "waiting_input"
  ) {
    return value
  }
  return null
}

function normalizePendingMessageCount(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return 0
  return Math.floor(value)
}

function normalizeKnownSandboxStatus(value: unknown): KnownSandboxStatus | null {
  if (
    value === "none" ||
    value === "creating" ||
    value === "starting_services" ||
    value === "starting_agent" ||
    value === "starting_opencode" ||
    value === "connecting_sse" ||
    value === "running" ||
    value === "stopped" ||
    value === "error"
  ) {
    return value
  }

  return null
}

function normalizeLifecycleStatus(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizePersistedStatus(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizePrState(value: unknown): "draft" | "open" | "merged" | "closed" | null {
  if (value === "draft" || value === "open" || value === "merged" || value === "closed") {
    return value
  }
  return null
}

function normalizePrNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  return Math.floor(value)
}

function normalizeOptionalText(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizeYoloMergeFailureKind(value: unknown): YoloMergeFailureKind | null {
  if (
    value === "conflicting" ||
    value === "behind" ||
    value === "blocked" ||
    value === "draft" ||
    value === "unknown"
  ) {
    return value
  }

  return null
}

function normalizePrMergeableState(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim().toLowerCase()
  return trimmed.length > 0 ? trimmed : null
}

function normalizePrFlowRuntimeStep(value: unknown): PrFlowRuntimeStep {
  if (
    value === "validating" ||
    value === "queued" ||
    value === "resuming_sandbox" ||
    value === "awaiting_draft" ||
    value === "awaiting_finalize" ||
    value === "staging" ||
    value === "committing" ||
    value === "pushing" ||
    value === "creating_pr" ||
    value === "linking_existing_pr" ||
    value === "success" ||
    value === "failure"
  ) {
    return value
  }

  return "idle"
}

function normalizeViewedEventId(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return 0
  return Math.floor(value)
}

function mergeNonRegressingEventId(current: number | null, incoming: number | null): number | null {
  if (current === null) return incoming
  if (incoming === null) return current
  return Math.max(current, incoming)
}

function mergeNonRegressingTimestamp(current: number | null, incoming: number | null): number | null {
  if (current === null) return incoming
  if (incoming === null) return current
  return Math.max(current, incoming)
}

function hasStrongerIncomingStatusRevision(args: {
  existing: ThreadRuntimeEntry
  activityEventId: number | null
  finishedEventId: number | null
}): boolean {
  if (
    args.activityEventId !== null &&
    (
      args.existing.lastActivityEventId === null ||
      args.activityEventId > args.existing.lastActivityEventId
    )
  ) {
    return true
  }

  return (
    args.finishedEventId !== null &&
    (
      args.existing.lastFinishedEventId === null ||
      args.finishedEventId > args.existing.lastFinishedEventId
    )
  )
}

function isIncomingEventRevisionCurrentOrNewer(args: {
  existingEventId: number | null
  incomingEventId: number | null
}): boolean {
  if (args.incomingEventId === null) {
    return args.existingEventId === null
  }

  return args.existingEventId === null || args.incomingEventId >= args.existingEventId
}

function mergeFinishReason(
  currentReason: FinishReason,
  currentFinishedEventId: number | null,
  incomingReason: FinishReason,
  incomingFinishedEventId: number | null,
): FinishReason {
  if (incomingFinishedEventId === null) return currentReason
  if (currentFinishedEventId === null) return incomingReason
  if (incomingFinishedEventId > currentFinishedEventId) return incomingReason
  if (
    incomingFinishedEventId === currentFinishedEventId &&
    currentReason === null &&
    incomingReason !== null
  ) {
    return incomingReason
  }
  return currentReason
}

function getStoredViewedEventId(args: {
  viewedEventIds: Record<string, number>
  sessionId: string
}): number {
  return normalizeViewedEventId(args.viewedEventIds[args.sessionId])
}

function createEntry(args: {
  sessionId: string
  initialViewedEventId?: number
}): ThreadRuntimeEntry {
  return {
    sessionId: args.sessionId,
    state: "idle",
    lastActivityEventId: null,
    lastFinishedEventId: null,
    lastFinishedAt: null,
    lastFinishReason: null,
    rawTailId: null,
    terminalRawCursor: null,
    terminalRawCursorObservedAt: null,
    lastViewedEventId: normalizeViewedEventId(args.initialViewedEventId),
    activityStateObservedAt: null,
    lifecycleStatusObservedAt: null,
    prStateObservedAt: null,
    prMergeabilityObservedAt: null,
    lastKnownPersistedStatus: null,
    lastKnownLifecycleStatus: null,
    lastKnownPrNumber: null,
    lastKnownPrUrl: null,
    lastKnownPrState: null,
    lastKnownPrMergeable: null,
    lastKnownPrMergeableState: null,
    lastKnownPrMergeabilityKnown: false,
    lastKnownYoloMergeFailureKind: null,
    yoloMergeFailureObservedAt: null,
    optimisticWorkRequestPending: false,
    pendingMessageCount: 0,
    latestPendingWorkTimestamp: null,
    optimisticYoloStartPending: false,
    yoloFlowStep: "idle",
    yoloFlowUpdatedAt: null,
    prFlowOperationKind: "none",
    prFlowOperationStatus: "idle",
    prFlowOperationStep: "idle",
    prFlowOperationUpdatedAt: null,
    lastKnownSandboxStatus: null,
    recoveryStatusObservedAt: null,
    lastKnownRecoveryStatus: null,
  }
}

const optimisticWorkRequestTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
const optimisticYoloStartTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

function clearOptimisticWorkRequestTimeout(sessionId: string): void {
  const existingTimeout = optimisticWorkRequestTimeouts.get(sessionId)
  if (!existingTimeout) return
  clearTimeout(existingTimeout)
  optimisticWorkRequestTimeouts.delete(sessionId)
}

function clearOptimisticYoloStartTimeout(sessionId: string): void {
  const existingTimeout = optimisticYoloStartTimeouts.get(sessionId)
  if (!existingTimeout) return
  clearTimeout(existingTimeout)
  optimisticYoloStartTimeouts.delete(sessionId)
}

export function isThreadRuntimeYoloSpinnerActive(
  entry:
    | Pick<ThreadRuntimeEntry, "optimisticYoloStartPending" | "yoloFlowStep">
    | undefined,
): boolean {
  if (!entry) return false
  return entry.optimisticYoloStartPending || isYoloFlowStepActive(entry.yoloFlowStep)
}

export function isYoloFlowStepActive(step: unknown): boolean {
  return step === "quick_pr" || step === "merge_pr" || step === "archiving"
}

export function isThreadRuntimePrFlowSpinnerActive(
  entry:
    | Pick<ThreadRuntimeEntry, "prFlowOperationKind" | "prFlowOperationStatus">
    | undefined,
): boolean {
  if (!entry) return false
  const isCreateOrUpdateKind =
    entry.prFlowOperationKind === "manual_create_pr" ||
    entry.prFlowOperationKind === "quick_pr" ||
    entry.prFlowOperationKind === "update_pr"
  return isCreateOrUpdateKind &&
    (entry.prFlowOperationStatus === "accepted" || entry.prFlowOperationStatus === "running")
}

function buildNextThreadRuntimeEntry(
  args: {
    existing: ThreadRuntimeEntry
    input: ApplyThreadActivityInput
  },
): ThreadRuntimeEntry | null {
  const eventId = normalizeEventId(args.input.eventId)
  if (!eventId) return null
  if (args.existing.lastActivityEventId && eventId <= args.existing.lastActivityEventId) {
    return null
  }

  const incomingFinishedEventId = normalizeEventId(args.input.finishedEventId)
  const incomingFinishedAt = normalizeTimestamp(args.input.finishedAt)
  const incomingFinishReason = normalizeFinishReason(args.input.finishReason)
  const incomingRawTailId = normalizeEventId(args.input.rawTailId)
  const incomingTerminalRawCursor = normalizeEventId(args.input.terminalRawCursor)
  const incomingTerminalRawCursorObservedAt = normalizeTimestamp(args.input.terminalRawCursorObservedAt)
  const nextFinishedEventId = mergeNonRegressingEventId(
    args.existing.lastFinishedEventId,
    incomingFinishedEventId,
  )
  const nextFinishedAt = mergeNonRegressingTimestamp(
    args.existing.lastFinishedAt,
    incomingFinishedAt,
  )
  const nextFinishReason = mergeFinishReason(
    args.existing.lastFinishReason,
    args.existing.lastFinishedEventId,
    incomingFinishReason,
    incomingFinishedEventId,
  )
  const nextState = normalizeRuntimeState(args.input.state)
  const shouldClearOptimisticWorkRequest =
    nextState === "running" ||
    nextState === "error" ||
    incomingFinishedEventId !== null

  return {
    ...args.existing,
    state: nextState,
    lastActivityEventId: eventId,
    lastFinishedEventId: nextFinishedEventId,
    lastFinishedAt: nextFinishedAt,
    lastFinishReason: nextFinishReason,
    rawTailId: mergeNonRegressingEventId(args.existing.rawTailId, incomingRawTailId),
    terminalRawCursor: incomingTerminalRawCursor ?? args.existing.terminalRawCursor,
    terminalRawCursorObservedAt: mergeNonRegressingTimestamp(
      args.existing.terminalRawCursorObservedAt,
      incomingTerminalRawCursorObservedAt,
    ),
    optimisticWorkRequestPending: shouldClearOptimisticWorkRequest
      ? false
      : args.existing.optimisticWorkRequestPending,
  }
}

function computeNextThreadStatusEntry(args: {
  existing: ThreadRuntimeEntry
  input: ApplyThreadStatusInput
}): ComputeThreadStatusEntryResult {
  const observedAt = normalizeTimestamp(args.input.observedAt)
  const statusEventId = normalizeEventId(args.input.lastActivityEventId)
  const incomingFinishedEventId = normalizeEventId(args.input.lastFinishedEventId)
  const incomingFinishedAt = normalizeTimestamp(args.input.lastFinishedAt)
  const incomingFinishReason = normalizeFinishReason(args.input.lastFinishReason)
  const incomingRawTailId = normalizeEventId(args.input.rawTailId)
  const incomingTerminalRawCursor = normalizeEventId(args.input.terminalRawCursor)
  const incomingTerminalRawCursorObservedAt = normalizeTimestamp(args.input.terminalRawCursorObservedAt)
  const normalizedSandboxStatus = normalizeKnownSandboxStatus(args.input.sandboxStatus)
  const normalizedPersistedStatus = normalizePersistedStatus(args.input.persistedStatus)
  const normalizedLifecycleStatus = normalizeLifecycleStatus(args.input.lifecycleStatus)
  const hasIncomingActivityObservation =
    args.input.activityState !== undefined ||
    args.input.lastActivityEventId !== undefined ||
    args.input.lastFinishedEventId !== undefined ||
    args.input.lastFinishedAt !== undefined ||
    args.input.lastFinishReason !== undefined ||
    args.input.rawTailId !== undefined ||
    args.input.terminalRawCursor !== undefined ||
    args.input.terminalRawCursorObservedAt !== undefined
  const hasIncomingActivityStatePayload =
    args.input.activityState !== undefined || args.input.lastActivityEventId !== undefined
  const hasIncomingFinishPayload =
    args.input.lastFinishedEventId !== undefined ||
    args.input.lastFinishedAt !== undefined ||
    args.input.lastFinishReason !== undefined ||
    args.input.terminalRawCursor !== undefined ||
    args.input.terminalRawCursorObservedAt !== undefined
  const hasStrongerEventRevision = hasStrongerIncomingStatusRevision({
    existing: args.existing,
    activityEventId: statusEventId,
    finishedEventId: incomingFinishedEventId,
  })
  const hasExistingComparableActivityRevision =
    args.existing.lastActivityEventId !== null || args.existing.lastFinishedEventId !== null
  const hasCurrentSnapshotRevision =
    args.existing.activityStateObservedAt !== null ||
    args.existing.lastActivityEventId !== null ||
    args.existing.lastFinishedEventId !== null ||
    args.existing.lastKnownSandboxStatus !== null
  const hasIncomingSnapshotPayload =
    hasIncomingActivityObservation || normalizedSandboxStatus !== null
  const snapshotObservedAtIsCurrentOrNewer =
    observedAt !== null &&
    hasIncomingSnapshotPayload &&
    (
      args.existing.activityStateObservedAt === null ||
      observedAt >= args.existing.activityStateObservedAt
    )
  const snapshotAccepted = hasStrongerEventRevision ||
    snapshotObservedAtIsCurrentOrNewer ||
    (!hasCurrentSnapshotRevision && hasIncomingSnapshotPayload)
  const activityStateAccepted =
    snapshotAccepted &&
    hasIncomingActivityStatePayload &&
    (
      !hasExistingComparableActivityRevision ||
      isIncomingEventRevisionCurrentOrNewer({
        existingEventId: args.existing.lastActivityEventId,
        incomingEventId: statusEventId,
      })
    )
  const finishAccepted =
    snapshotAccepted &&
    hasIncomingFinishPayload &&
    (
      !hasExistingComparableActivityRevision ||
      isIncomingEventRevisionCurrentOrNewer({
        existingEventId: args.existing.lastFinishedEventId,
        incomingEventId: incomingFinishedEventId,
      })
    )
  const activityAccepted = activityStateAccepted || finishAccepted
  const sandboxAccepted = normalizedSandboxStatus !== null && snapshotAccepted
  const hasIncomingLifecyclePayload =
    args.input.persistedStatus !== undefined || args.input.lifecycleStatus !== undefined
  const lifecycleAccepted =
    hasIncomingLifecyclePayload &&
    (
      observedAt !== null
        ? (
            args.existing.lifecycleStatusObservedAt === null ||
            observedAt >= args.existing.lifecycleStatusObservedAt
          )
        : args.existing.lifecycleStatusObservedAt === null
    )

  const result: ApplyThreadStatusResult = {
    snapshotAccepted,
    activityAccepted,
    sandboxAccepted,
    lifecycleAccepted,
  }

  if (!snapshotAccepted && !lifecycleAccepted) {
    return {
      result,
      next: null,
      shouldClearOptimisticWorkRequest: false,
    }
  }

  const nextActivityEventId = activityStateAccepted
    ? mergeNonRegressingEventId(args.existing.lastActivityEventId, statusEventId)
    : args.existing.lastActivityEventId
  const nextFinishedEventId = finishAccepted
    ? mergeNonRegressingEventId(args.existing.lastFinishedEventId, incomingFinishedEventId)
    : args.existing.lastFinishedEventId
  const nextFinishedAt = finishAccepted
    ? mergeNonRegressingTimestamp(args.existing.lastFinishedAt, incomingFinishedAt)
    : args.existing.lastFinishedAt
  const nextFinishReason = finishAccepted
    ? mergeFinishReason(
        args.existing.lastFinishReason,
        args.existing.lastFinishedEventId,
        incomingFinishReason,
        incomingFinishedEventId,
      )
    : args.existing.lastFinishReason
  const rawCursorAccepted = snapshotAccepted && (
    args.input.rawTailId !== undefined ||
    args.input.terminalRawCursor !== undefined ||
    args.input.terminalRawCursorObservedAt !== undefined
  )
  const nextRawTailId = rawCursorAccepted
    ? mergeNonRegressingEventId(args.existing.rawTailId, incomingRawTailId)
    : args.existing.rawTailId
  const nextTerminalRawCursor = rawCursorAccepted && args.input.terminalRawCursor !== undefined
    ? incomingTerminalRawCursor
    : args.existing.terminalRawCursor
  const nextTerminalRawCursorObservedAt = rawCursorAccepted && args.input.terminalRawCursorObservedAt !== undefined
    ? mergeNonRegressingTimestamp(args.existing.terminalRawCursorObservedAt, incomingTerminalRawCursorObservedAt)
    : args.existing.terminalRawCursorObservedAt
  const nextState = activityStateAccepted && args.input.activityState !== undefined
    ? normalizeRuntimeState(args.input.activityState)
    : args.existing.state
  const shouldClearOptimisticWorkRequest =
    nextState === "running" ||
    nextState === "error" ||
    nextFinishedEventId !== args.existing.lastFinishedEventId
  const nextSandboxStatus = sandboxAccepted
    ? normalizedSandboxStatus
    : args.existing.lastKnownSandboxStatus
  const nextActivityStateObservedAt =
    (activityAccepted || sandboxAccepted) && observedAt !== null
      ? mergeNonRegressingTimestamp(args.existing.activityStateObservedAt, observedAt)
      : args.existing.activityStateObservedAt
  const nextPersistedStatus =
    lifecycleAccepted && args.input.persistedStatus !== undefined
      ? normalizedPersistedStatus
      : args.existing.lastKnownPersistedStatus
  const nextLifecycleStatus =
    lifecycleAccepted && args.input.lifecycleStatus !== undefined
      ? normalizedLifecycleStatus
      : args.existing.lastKnownLifecycleStatus
  const nextLifecycleStatusObservedAt =
    lifecycleAccepted && observedAt !== null
      ? mergeNonRegressingTimestamp(args.existing.lifecycleStatusObservedAt, observedAt)
      : args.existing.lifecycleStatusObservedAt

  if (
    args.existing.state === nextState &&
    args.existing.lastActivityEventId === nextActivityEventId &&
    args.existing.lastFinishedEventId === nextFinishedEventId &&
    args.existing.lastFinishedAt === nextFinishedAt &&
    args.existing.lastFinishReason === nextFinishReason &&
    args.existing.rawTailId === nextRawTailId &&
    args.existing.terminalRawCursor === nextTerminalRawCursor &&
    args.existing.terminalRawCursorObservedAt === nextTerminalRawCursorObservedAt &&
    args.existing.lastKnownSandboxStatus === nextSandboxStatus &&
    args.existing.activityStateObservedAt === nextActivityStateObservedAt &&
    args.existing.lastKnownPersistedStatus === nextPersistedStatus &&
    args.existing.lastKnownLifecycleStatus === nextLifecycleStatus &&
    args.existing.lifecycleStatusObservedAt === nextLifecycleStatusObservedAt &&
    args.existing.optimisticWorkRequestPending === (
      shouldClearOptimisticWorkRequest ? false : args.existing.optimisticWorkRequestPending
    )
  ) {
    return {
      result,
      next: null,
      shouldClearOptimisticWorkRequest: false,
    }
  }

  return {
    result,
    shouldClearOptimisticWorkRequest,
    next: {
      ...args.existing,
      state: nextState,
      lastActivityEventId: nextActivityEventId,
      lastFinishedEventId: nextFinishedEventId,
      lastFinishedAt: nextFinishedAt,
      lastFinishReason: nextFinishReason,
      rawTailId: nextRawTailId,
      terminalRawCursor: nextTerminalRawCursor,
      terminalRawCursorObservedAt: nextTerminalRawCursorObservedAt,
      activityStateObservedAt: nextActivityStateObservedAt,
      lifecycleStatusObservedAt: nextLifecycleStatusObservedAt,
      lastKnownPersistedStatus: nextPersistedStatus,
      lastKnownLifecycleStatus: nextLifecycleStatus,
      optimisticWorkRequestPending: shouldClearOptimisticWorkRequest
        ? false
        : args.existing.optimisticWorkRequestPending,
      lastKnownSandboxStatus: nextSandboxStatus,
    },
  }
}

function computeNextThreadPrStateEntry(args: {
  existing: ThreadRuntimeEntry
  input: ApplyThreadPrStateInput
}): ThreadRuntimeEntry | null {
  return computeNextThreadPrStatusEntry({
    existing: args.existing,
    input: {
      sessionId: args.input.sessionId,
      prState: args.input.prState,
      mergeableState: args.input.mergeableState,
      mergeabilityKnown: args.input.mergeabilityKnown,
      observedAt: args.input.observedAt,
    },
  })
}

function computeNextThreadPrStatusEntry(args: {
  existing: ThreadRuntimeEntry
  input: ApplyThreadPrStatusInput
}): ThreadRuntimeEntry | null {
  const observedAt = normalizeTimestamp(args.input.observedAt)
  const hasLifecyclePayload =
    args.input.prNumber !== undefined ||
    args.input.prUrl !== undefined ||
    args.input.prState !== undefined
  const hasMergeabilityPayload =
    args.input.mergeable !== undefined ||
    args.input.mergeableState !== undefined ||
    args.input.mergeabilityKnown !== undefined

  const lifecycleAccepted = hasLifecyclePayload && (
    observedAt !== null
      ? args.existing.prStateObservedAt === null || observedAt >= args.existing.prStateObservedAt
      : args.existing.prStateObservedAt === null && args.existing.lastKnownPrState === null
  )
  const mergeabilityAccepted = hasMergeabilityPayload && (
    observedAt !== null
      ? args.existing.prMergeabilityObservedAt === null || observedAt >= args.existing.prMergeabilityObservedAt
      : args.existing.prMergeabilityObservedAt === null && !args.existing.lastKnownPrMergeabilityKnown
  )

  if (!lifecycleAccepted && !mergeabilityAccepted) {
    return null
  }

  const nextPrNumber = lifecycleAccepted && args.input.prNumber !== undefined
    ? normalizePrNumber(args.input.prNumber)
    : args.existing.lastKnownPrNumber
  const nextPrUrl = lifecycleAccepted && args.input.prUrl !== undefined
    ? normalizeOptionalText(args.input.prUrl)
    : args.existing.lastKnownPrUrl
  const nextPrState = lifecycleAccepted && args.input.prState !== undefined
    ? normalizePrState(args.input.prState)
    : args.existing.lastKnownPrState

  const prNumberChanged = lifecycleAccepted &&
    args.input.prNumber !== undefined &&
    args.existing.lastKnownPrNumber !== null &&
    nextPrNumber !== args.existing.lastKnownPrNumber

  const nextMergeabilityKnown = mergeabilityAccepted
    ? args.input.mergeabilityKnown === true
    : prNumberChanged
      ? false
      : args.existing.lastKnownPrMergeabilityKnown
  const nextMergeable = mergeabilityAccepted
    ? nextMergeabilityKnown && typeof args.input.mergeable === "boolean"
      ? args.input.mergeable
      : null
    : prNumberChanged
      ? null
      : args.existing.lastKnownPrMergeable
  const nextMergeableState = mergeabilityAccepted
    ? nextMergeabilityKnown
      ? normalizePrMergeableState(args.input.mergeableState)
      : null
    : prNumberChanged
      ? null
      : args.existing.lastKnownPrMergeableState

  const nextIsTerminalPr = nextPrState === "merged" || nextPrState === "closed"
  const shouldClearMergeFailureHint = prNumberChanged || nextIsTerminalPr
  const nextPrMergeabilityObservedAt = mergeabilityAccepted && observedAt !== null
    ? observedAt
    : prNumberChanged
      ? null
      : args.existing.prMergeabilityObservedAt

  if (
    args.existing.lastKnownPrNumber === nextPrNumber &&
    args.existing.lastKnownPrUrl === nextPrUrl &&
    args.existing.lastKnownPrState === nextPrState &&
    args.existing.lastKnownPrMergeable === nextMergeable &&
    args.existing.lastKnownPrMergeableState === nextMergeableState &&
    args.existing.lastKnownPrMergeabilityKnown === nextMergeabilityKnown &&
    args.existing.prStateObservedAt === (lifecycleAccepted && observedAt !== null ? observedAt : args.existing.prStateObservedAt) &&
    args.existing.prMergeabilityObservedAt === nextPrMergeabilityObservedAt &&
    (!shouldClearMergeFailureHint || args.existing.lastKnownYoloMergeFailureKind === null)
  ) {
    return null
  }

  return {
    ...args.existing,
    lastKnownPrNumber: nextPrNumber,
    lastKnownPrUrl: nextPrUrl,
    lastKnownPrState: nextPrState,
    lastKnownPrMergeable: nextMergeable,
    lastKnownPrMergeableState: nextMergeableState,
    lastKnownPrMergeabilityKnown: nextMergeabilityKnown,
    prStateObservedAt: lifecycleAccepted && observedAt !== null
      ? observedAt
      : args.existing.prStateObservedAt,
    prMergeabilityObservedAt: nextPrMergeabilityObservedAt,
    lastKnownYoloMergeFailureKind: shouldClearMergeFailureHint
      ? null
      : args.existing.lastKnownYoloMergeFailureKind,
    yoloMergeFailureObservedAt: shouldClearMergeFailureHint
      ? null
      : args.existing.yoloMergeFailureObservedAt,
  }
}

function computeNextThreadPrMergeFailureHintEntry(args: {
  existing: ThreadRuntimeEntry
  input: ApplyThreadPrMergeFailureHintInput
}): ThreadRuntimeEntry | null {
  const observedAt = normalizeTimestamp(args.input.observedAt)
  const mergeFailureKind = normalizeYoloMergeFailureKind(args.input.mergeFailureKind)
  if (mergeFailureKind === null) return null
  if (
    observedAt !== null &&
    args.existing.yoloMergeFailureObservedAt !== null &&
    observedAt < args.existing.yoloMergeFailureObservedAt
  ) {
    return null
  }
  if (
    args.existing.lastKnownYoloMergeFailureKind === mergeFailureKind &&
    (observedAt === null || observedAt === args.existing.yoloMergeFailureObservedAt)
  ) {
    return null
  }

  return {
    ...args.existing,
    lastKnownYoloMergeFailureKind: mergeFailureKind,
    yoloMergeFailureObservedAt: observedAt ?? args.existing.yoloMergeFailureObservedAt,
  }
}

function normalizeThreadRuntimeRecoveryStatus(
  value: ThreadRuntimeRecoveryStatus | null | undefined,
): ThreadRuntimeRecoveryStatus | null {
  if (!value || typeof value !== "object") return null
  if (value.level !== "warning" && value.level !== "blocked") return null
  const label = typeof value.label === "string" ? value.label.trim() : ""
  if (label.length === 0) return null
  const detail = typeof value.detail === "string" && value.detail.trim().length > 0
    ? value.detail.trim()
    : null

  return {
    level: value.level,
    label,
    detail,
  }
}

function areThreadRuntimeRecoveryStatusesEqual(args: {
  previous: ThreadRuntimeRecoveryStatus | null
  next: ThreadRuntimeRecoveryStatus | null
}): boolean {
  return args.previous?.level === args.next?.level
    && args.previous?.label === args.next?.label
    && args.previous?.detail === args.next?.detail
}

function computeNextThreadRecoveryStatusEntry(args: {
  existing: ThreadRuntimeEntry
  input: ApplyThreadRecoveryStatusInput
}): ThreadRuntimeEntry | null {
  const normalizedObservedAt = normalizeTimestamp(args.input.observedAt)
  const normalizedRecoveryStatus = normalizeThreadRuntimeRecoveryStatus(args.input.recoveryStatus)

  if (
    normalizedObservedAt !== null
    && args.existing.recoveryStatusObservedAt !== null
    && normalizedObservedAt < args.existing.recoveryStatusObservedAt
  ) {
    return null
  }

  if (
    normalizedObservedAt === null
    && args.existing.recoveryStatusObservedAt !== null
    && args.existing.lastKnownRecoveryStatus !== null
  ) {
    return null
  }

  if (
    areThreadRuntimeRecoveryStatusesEqual({
      previous: args.existing.lastKnownRecoveryStatus,
      next: normalizedRecoveryStatus,
    })
    && (normalizedObservedAt === null || normalizedObservedAt === args.existing.recoveryStatusObservedAt)
  ) {
    return null
  }

  return {
    ...args.existing,
    lastKnownRecoveryStatus: normalizedRecoveryStatus,
    recoveryStatusObservedAt: normalizedObservedAt ?? args.existing.recoveryStatusObservedAt,
  }
}

type ThreadRuntimeEntriesRevisionState = Pick<
  ThreadRuntimeStore,
  "backgroundWarmingEntriesRevision" | "notificationEntriesRevision" | "faviconEntriesRevision"
>

function didBackgroundWarmingRelevantEntryChange(args: {
  previous: ThreadRuntimeEntry
  next: ThreadRuntimeEntry
}): boolean {
  return (
    args.previous.state !== args.next.state ||
    args.previous.lastActivityEventId !== args.next.lastActivityEventId ||
    args.previous.lastFinishedEventId !== args.next.lastFinishedEventId ||
    args.previous.lastFinishReason !== args.next.lastFinishReason ||
    args.previous.lastViewedEventId !== args.next.lastViewedEventId ||
    args.previous.activityStateObservedAt !== args.next.activityStateObservedAt ||
    args.previous.optimisticWorkRequestPending !== args.next.optimisticWorkRequestPending ||
    args.previous.pendingMessageCount !== args.next.pendingMessageCount ||
    args.previous.lastKnownSandboxStatus !== args.next.lastKnownSandboxStatus
  )
}

function didNotificationRelevantEntryChange(args: {
  previous: ThreadRuntimeEntry
  next: ThreadRuntimeEntry
}): boolean {
  return (
    args.previous.state !== args.next.state ||
    args.previous.lastFinishedEventId !== args.next.lastFinishedEventId ||
    args.previous.lastFinishedAt !== args.next.lastFinishedAt ||
    args.previous.lastFinishReason !== args.next.lastFinishReason
  )
}

function didFaviconRelevantEntryChange(args: {
  previous: ThreadRuntimeEntry
  next: ThreadRuntimeEntry
}): boolean {
  return (
    args.previous.state !== args.next.state ||
    args.previous.lastActivityEventId !== args.next.lastActivityEventId ||
    args.previous.lastFinishedEventId !== args.next.lastFinishedEventId ||
    args.previous.lastFinishedAt !== args.next.lastFinishedAt ||
    args.previous.lastFinishReason !== args.next.lastFinishReason ||
    args.previous.lastViewedEventId !== args.next.lastViewedEventId ||
    args.previous.activityStateObservedAt !== args.next.activityStateObservedAt ||
    args.previous.optimisticWorkRequestPending !== args.next.optimisticWorkRequestPending ||
    args.previous.pendingMessageCount !== args.next.pendingMessageCount ||
    args.previous.latestPendingWorkTimestamp !== args.next.latestPendingWorkTimestamp ||
    args.previous.lastKnownSandboxStatus !== args.next.lastKnownSandboxStatus ||
    args.previous.lastKnownPersistedStatus !== args.next.lastKnownPersistedStatus ||
    args.previous.lastKnownLifecycleStatus !== args.next.lastKnownLifecycleStatus
  )
}

function buildEntriesRevisionPatch(args: {
  state: ThreadRuntimeEntriesRevisionState
  changedEntries: Array<{
    previous: ThreadRuntimeEntry
    next: ThreadRuntimeEntry
  }>
}): ThreadRuntimeEntriesRevisionState {
  let backgroundWarmingEntriesRevision = args.state.backgroundWarmingEntriesRevision
  let notificationEntriesRevision = args.state.notificationEntriesRevision
  let faviconEntriesRevision = args.state.faviconEntriesRevision
  let backgroundWarmingChanged = false
  let notificationChanged = false
  let faviconChanged = false

  for (const changedEntry of args.changedEntries) {
    if (
      !backgroundWarmingChanged &&
      didBackgroundWarmingRelevantEntryChange(changedEntry)
    ) {
      backgroundWarmingChanged = true
      backgroundWarmingEntriesRevision += 1
    }
    if (
      !notificationChanged &&
      didNotificationRelevantEntryChange(changedEntry)
    ) {
      notificationChanged = true
      notificationEntriesRevision += 1
    }
    if (
      !faviconChanged &&
      didFaviconRelevantEntryChange(changedEntry)
    ) {
      faviconChanged = true
      faviconEntriesRevision += 1
    }
    if (backgroundWarmingChanged && notificationChanged && faviconChanged) {
      break
    }
  }

  return {
    backgroundWarmingEntriesRevision,
    notificationEntriesRevision,
    faviconEntriesRevision,
  }
}

function buildEntriesRemovalRevisionPatch(args: {
  state: ThreadRuntimeEntriesRevisionState
}): ThreadRuntimeEntriesRevisionState {
  return {
    backgroundWarmingEntriesRevision: args.state.backgroundWarmingEntriesRevision + 1,
    notificationEntriesRevision: args.state.notificationEntriesRevision + 1,
    faviconEntriesRevision: args.state.faviconEntriesRevision + 1,
  }
}

function normalizePrFlowOperationInput(args: {
  existing: ThreadRuntimeEntry
  input: SetPrFlowOperationInput
}): {
  kind: PrFlowRuntimeKind
  status: PrFlowRuntimeStatus
  step: PrFlowRuntimeStep
  timestamp: number | null
} {
  const normalizedKind: PrFlowRuntimeKind =
    args.input.kind === "manual_create_pr" ||
    args.input.kind === "quick_pr" ||
    args.input.kind === "update_pr" ||
    args.input.kind === "merge_pr" ||
    args.input.kind === "archive_thread" ||
    args.input.kind === "yolo"
      ? args.input.kind
      : "none"

  const normalizedStatus: PrFlowRuntimeStatus =
    args.input.status === "accepted" ||
    args.input.status === "running" ||
    args.input.status === "succeeded" ||
    args.input.status === "failed" ||
    args.input.status === "cancelled" ||
    args.input.status === "reconciled"
      ? args.input.status
      : "idle"

  return {
    kind: normalizedKind,
    status: normalizedStatus,
    step:
      args.input.step === undefined
        ? args.existing.prFlowOperationStep
        : normalizePrFlowRuntimeStep(args.input.step),
    timestamp: normalizeTimestamp(args.input.timestamp),
  }
}

function computeNextPrFlowOperationEntry(args: {
  existing: ThreadRuntimeEntry
  input: SetPrFlowOperationInput
}): ThreadRuntimeEntry | null {
  const normalized = normalizePrFlowOperationInput(args)

  if (normalized.timestamp === null && args.existing.prFlowOperationUpdatedAt !== null) {
    return null
  }

  if (
    normalized.timestamp !== null &&
    args.existing.prFlowOperationUpdatedAt !== null &&
    normalized.timestamp < args.existing.prFlowOperationUpdatedAt
  ) {
    return null
  }

  if (
    args.existing.prFlowOperationKind === normalized.kind &&
    args.existing.prFlowOperationStatus === normalized.status &&
    args.existing.prFlowOperationStep === normalized.step &&
    (normalized.timestamp === null || normalized.timestamp === args.existing.prFlowOperationUpdatedAt)
  ) {
    return null
  }

  return {
    ...args.existing,
    prFlowOperationKind: normalized.kind,
    prFlowOperationStatus: normalized.status,
    prFlowOperationStep: normalized.step,
    prFlowOperationUpdatedAt: normalized.timestamp ?? args.existing.prFlowOperationUpdatedAt,
  }
}

export function deriveThreadDisplayState(
  entry: ThreadRuntimeEntry | undefined,
  fallbackStatus?: string | null,
  lifecycleStatus?: string | null,
): ThreadDisplayState {
  const effectiveLifecycleStatus = entry?.lastKnownLifecycleStatus ?? lifecycleStatus
  const effectiveFallbackStatus = entry?.lastKnownPersistedStatus ?? fallbackStatus
  const hasObservedLiveActivity =
    entry !== undefined &&
    (
      entry.activityStateObservedAt !== null ||
      entry.lastActivityEventId !== null ||
      entry.lastFinishedEventId !== null
    )
  const hasPendingRequestedWork =
    entry !== undefined && (entry.pendingMessageCount > 0 || entry.optimisticWorkRequestPending)

  if (effectiveLifecycleStatus === "suspended" || effectiveLifecycleStatus === "suspending") {
    return "suspended"
  }

  if (entry) {
    if (entry.state === "running") return "running"
    if (hasPendingRequestedWork) return "running"
    if (entry.state === "error" && hasObservedLiveActivity) return "error"
    if (entry.lastFinishReason === "waiting_input") return "waiting"
    if (
      typeof entry.lastFinishedEventId === "number" &&
      entry.lastFinishedEventId > entry.lastViewedEventId
    ) {
      return "waiting"
    }
    if (hasObservedLiveActivity) return "idle"
    if (effectiveFallbackStatus === "suspended") return "suspended"
  }

  if (effectiveFallbackStatus === "running") return "running"
  if (effectiveFallbackStatus === "error") return "error"
  if (effectiveFallbackStatus === "suspended") return "suspended"
  return "idle"
}

export const useThreadRuntime = create<ThreadRuntimeStore>((set, get) => ({
  entries: {},
  backgroundWarmingEntriesRevision: 0,
  notificationEntriesRevision: 0,
  faviconEntriesRevision: 0,
  viewedEventIds: {},

  applyThreadActivity: (input) => {
    set((state) => {
      const existing = state.entries[input.sessionId] ?? createEntry({
        sessionId: input.sessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: input.sessionId,
        }),
      })
      const next = buildNextThreadRuntimeEntry({ existing, input })
      if (!next) return state

      if (next.optimisticWorkRequestPending !== existing.optimisticWorkRequestPending) {
        clearOptimisticWorkRequestTimeout(input.sessionId)
      }

      return {
        entries: {
          ...state.entries,
          [input.sessionId]: next,
        },
        ...buildEntriesRevisionPatch({
          state,
          changedEntries: [{ previous: existing, next }],
        }),
      }
    })
  },

  applyThreadActivities: (inputs) => {
    if (!Array.isArray(inputs) || inputs.length === 0) return 0

    let appliedCount = 0
    set((state) => {
      let changed = false
      const nextEntries: Record<string, ThreadRuntimeEntry> = { ...state.entries }
      const changedEntries: Array<{ previous: ThreadRuntimeEntry; next: ThreadRuntimeEntry }> = []

      for (const input of inputs) {
        const sessionId = typeof input.sessionId === "string" ? input.sessionId.trim() : ""
        if (!sessionId) continue

        const existing = nextEntries[sessionId] ?? createEntry({
          sessionId,
          initialViewedEventId: getStoredViewedEventId({
            viewedEventIds: state.viewedEventIds,
            sessionId,
          }),
        })
        const next = buildNextThreadRuntimeEntry({
          existing,
          input: { ...input, sessionId },
        })
        if (!next) continue

        if (next.optimisticWorkRequestPending !== existing.optimisticWorkRequestPending) {
          clearOptimisticWorkRequestTimeout(sessionId)
        }

        nextEntries[sessionId] = next
        changedEntries.push({ previous: existing, next })
        appliedCount += 1
        changed = true
      }

      return changed
        ? {
            entries: nextEntries,
            ...buildEntriesRevisionPatch({
              state,
              changedEntries,
            }),
          }
        : state
    })

    return appliedCount
  },

  applyThreadStatus: (input) => {
    let result: ApplyThreadStatusResult = {
      snapshotAccepted: false,
      activityAccepted: false,
      sandboxAccepted: false,
      lifecycleAccepted: false,
    }
    set((state) => {
      const existing = state.entries[input.sessionId] ?? createEntry({
        sessionId: input.sessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: input.sessionId,
        }),
      })
      const computed = computeNextThreadStatusEntry({ existing, input })
      result = computed.result

      if (computed.next === null) {
        return state
      }

      if (computed.shouldClearOptimisticWorkRequest && existing.optimisticWorkRequestPending) {
        clearOptimisticWorkRequestTimeout(input.sessionId)
      }

      return {
        entries: {
          ...state.entries,
          [input.sessionId]: computed.next,
        },
        ...buildEntriesRevisionPatch({
          state,
          changedEntries: [{ previous: existing, next: computed.next }],
        }),
      }
    })
    return result
  },

  applyThreadStatuses: (inputs) => {
    if (!Array.isArray(inputs) || inputs.length === 0) {
      return { appliedCount: 0 }
    }

    let appliedCount = 0

    set((state) => {
      let changed = false
      const nextEntries: Record<string, ThreadRuntimeEntry> = { ...state.entries }
      const changedEntries: Array<{ previous: ThreadRuntimeEntry; next: ThreadRuntimeEntry }> = []

      for (const rawInput of inputs) {
        const normalizedSessionId =
          typeof rawInput.sessionId === "string" ? rawInput.sessionId.trim() : ""
        if (!normalizedSessionId) {
          continue
        }

        const input = { ...rawInput, sessionId: normalizedSessionId }
        const existing = nextEntries[normalizedSessionId] ?? createEntry({
          sessionId: normalizedSessionId,
          initialViewedEventId: getStoredViewedEventId({
            viewedEventIds: state.viewedEventIds,
            sessionId: normalizedSessionId,
          }),
        })
        const computed = computeNextThreadStatusEntry({ existing, input })

        if (computed.next === null) {
          continue
        }

        if (computed.shouldClearOptimisticWorkRequest && existing.optimisticWorkRequestPending) {
          clearOptimisticWorkRequestTimeout(normalizedSessionId)
        }

        nextEntries[normalizedSessionId] = computed.next
        changedEntries.push({ previous: existing, next: computed.next })
        appliedCount += 1
        changed = true
      }

      return changed
        ? {
            entries: nextEntries,
            ...buildEntriesRevisionPatch({
              state,
              changedEntries,
            }),
          }
        : state
    })

    return { appliedCount }
  },

  applyThreadPrState: (rawInput) => {
    const { sessionId, prState, observedAt } = rawInput
    const normalizedSessionId = typeof sessionId === "string" ? sessionId.trim() : ""
    if (!normalizedSessionId) return false

    let applied = false

    set((state) => {
      const existing = state.entries[normalizedSessionId] ?? createEntry({
        sessionId: normalizedSessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: normalizedSessionId,
        }),
      })

      const next = computeNextThreadPrStateEntry({
        existing,
        input: {
          sessionId: normalizedSessionId,
          prState,
          observedAt,
          ...(Object.prototype.hasOwnProperty.call(rawInput, "mergeableState")
            ? { mergeableState: rawInput.mergeableState }
            : {}),
          ...(Object.prototype.hasOwnProperty.call(rawInput, "mergeabilityKnown")
            ? { mergeabilityKnown: rawInput.mergeabilityKnown }
            : {}),
        },
      })
      if (next === null) {
        return state
      }

      applied = true
      return {
        entries: {
          ...state.entries,
          [normalizedSessionId]: next,
        },
      }
    })

    return applied
  },

  applyThreadPrStates: (inputs) => {
    if (!Array.isArray(inputs) || inputs.length === 0) return 0

    let appliedCount = 0

    set((state) => {
      let changed = false
      const nextEntries: Record<string, ThreadRuntimeEntry> = { ...state.entries }

      for (const rawInput of inputs) {
        const normalizedSessionId = typeof rawInput.sessionId === "string" ? rawInput.sessionId.trim() : ""
        if (!normalizedSessionId) continue

        const existing = nextEntries[normalizedSessionId] ?? createEntry({
          sessionId: normalizedSessionId,
          initialViewedEventId: getStoredViewedEventId({
            viewedEventIds: state.viewedEventIds,
            sessionId: normalizedSessionId,
          }),
        })
        const next = computeNextThreadPrStateEntry({
          existing,
          input: {
            ...rawInput,
            sessionId: normalizedSessionId,
          },
        })
        if (next === null) continue

        nextEntries[normalizedSessionId] = next
        appliedCount += 1
        changed = true
      }

      return changed ? { entries: nextEntries } : state
    })

    return appliedCount
  },

  applyThreadPrStatus: ({ sessionId, ...rest }) => {
    const normalizedSessionId = typeof sessionId === "string" ? sessionId.trim() : ""
    if (!normalizedSessionId) return false

    let applied = false

    set((state) => {
      const existing = state.entries[normalizedSessionId] ?? createEntry({
        sessionId: normalizedSessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: normalizedSessionId,
        }),
      })

      const next = computeNextThreadPrStatusEntry({
        existing,
        input: {
          ...rest,
          sessionId: normalizedSessionId,
        },
      })
      if (next === null) return state

      applied = true
      return {
        entries: {
          ...state.entries,
          [normalizedSessionId]: next,
        },
      }
    })

    return applied
  },

  applyThreadPrStatuses: (inputs) => {
    if (!Array.isArray(inputs) || inputs.length === 0) return 0

    let appliedCount = 0

    set((state) => {
      let changed = false
      const nextEntries: Record<string, ThreadRuntimeEntry> = { ...state.entries }

      for (const rawInput of inputs) {
        const normalizedSessionId = typeof rawInput.sessionId === "string" ? rawInput.sessionId.trim() : ""
        if (!normalizedSessionId) continue

        const existing = nextEntries[normalizedSessionId] ?? createEntry({
          sessionId: normalizedSessionId,
          initialViewedEventId: getStoredViewedEventId({
            viewedEventIds: state.viewedEventIds,
            sessionId: normalizedSessionId,
          }),
        })
        const next = computeNextThreadPrStatusEntry({
          existing,
          input: {
            ...rawInput,
            sessionId: normalizedSessionId,
          },
        })
        if (next === null) continue

        nextEntries[normalizedSessionId] = next
        appliedCount += 1
        changed = true
      }

      return changed ? { entries: nextEntries } : state
    })

    return appliedCount
  },

  applyThreadPrMergeFailureHint: ({ sessionId, mergeFailureKind, observedAt }) => {
    const normalizedSessionId = typeof sessionId === "string" ? sessionId.trim() : ""
    if (!normalizedSessionId) return false

    let applied = false

    set((state) => {
      const existing = state.entries[normalizedSessionId] ?? createEntry({
        sessionId: normalizedSessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: normalizedSessionId,
        }),
      })
      const next = computeNextThreadPrMergeFailureHintEntry({
        existing,
        input: {
          sessionId: normalizedSessionId,
          mergeFailureKind,
          observedAt,
        },
      })
      if (next === null) return state

      applied = true
      return {
        entries: {
          ...state.entries,
          [normalizedSessionId]: next,
        },
      }
    })

    return applied
  },

  applyThreadRecoveryStatus: ({ sessionId, recoveryStatus, observedAt }) => {
    const normalizedSessionId = typeof sessionId === "string" ? sessionId.trim() : ""
    if (!normalizedSessionId) return false

    let applied = false

    set((state) => {
      const existing = state.entries[normalizedSessionId] ?? createEntry({
        sessionId: normalizedSessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: normalizedSessionId,
        }),
      })

      const next = computeNextThreadRecoveryStatusEntry({
        existing,
        input: {
          sessionId: normalizedSessionId,
          recoveryStatus,
          observedAt,
        },
      })
      if (next === null) {
        return state
      }

      applied = true
      return {
        entries: {
          ...state.entries,
          [normalizedSessionId]: next,
        },
      }
    })

    return applied
  },

  applyThreadRecoveryStatuses: (inputs) => {
    if (!Array.isArray(inputs) || inputs.length === 0) return 0

    let appliedCount = 0

    set((state) => {
      let changed = false
      const nextEntries: Record<string, ThreadRuntimeEntry> = { ...state.entries }

      for (const rawInput of inputs) {
        const normalizedSessionId = typeof rawInput.sessionId === "string" ? rawInput.sessionId.trim() : ""
        if (!normalizedSessionId) continue

        const existing = nextEntries[normalizedSessionId] ?? createEntry({
          sessionId: normalizedSessionId,
          initialViewedEventId: getStoredViewedEventId({
            viewedEventIds: state.viewedEventIds,
            sessionId: normalizedSessionId,
          }),
        })
        const next = computeNextThreadRecoveryStatusEntry({
          existing,
          input: {
            ...rawInput,
            sessionId: normalizedSessionId,
          },
        })
        if (next === null) continue

        nextEntries[normalizedSessionId] = next
        appliedCount += 1
        changed = true
      }

      return changed ? { entries: nextEntries } : state
    })

    return appliedCount
  },

  setOptimisticWorkRequestPending: ({ sessionId, pending, timeoutMs }) => {
    const normalizedSessionId = typeof sessionId === "string" ? sessionId.trim() : ""
    if (!normalizedSessionId) return

    clearOptimisticWorkRequestTimeout(normalizedSessionId)
    if (pending && typeof timeoutMs === "number" && Number.isFinite(timeoutMs) && timeoutMs > 0) {
      const timeoutHandle = setTimeout(() => {
        useThreadRuntime.getState().setOptimisticWorkRequestPending({
          sessionId: normalizedSessionId,
          pending: false,
        })
      }, timeoutMs)
      optimisticWorkRequestTimeouts.set(normalizedSessionId, timeoutHandle)
    }

    set((state) => {
      const existing = state.entries[normalizedSessionId] ?? createEntry({
        sessionId: normalizedSessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: normalizedSessionId,
        }),
      })
      if (existing.optimisticWorkRequestPending === pending) return state

      const next = {
        ...existing,
        optimisticWorkRequestPending: pending,
      }

      return {
        entries: {
          ...state.entries,
          [normalizedSessionId]: next,
        },
        ...buildEntriesRevisionPatch({
          state,
          changedEntries: [{ previous: existing, next }],
        }),
      }
    })
  },

  setPendingMessageCount: ({ sessionId, pendingMessageCount, latestPendingWorkTimestamp }) => {
    const normalizedSessionId = typeof sessionId === "string" ? sessionId.trim() : ""
    if (!normalizedSessionId) return

    const normalizedPendingMessageCount = normalizePendingMessageCount(pendingMessageCount)
    const normalizedLatestPendingWorkTimestamp = latestPendingWorkTimestamp === undefined
      ? undefined
      : normalizedPendingMessageCount > 0
        ? normalizeTimestamp(latestPendingWorkTimestamp)
        : null

    set((state) => {
      const existing = state.entries[normalizedSessionId] ?? createEntry({
        sessionId: normalizedSessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: normalizedSessionId,
        }),
      })
      const nextLatestPendingWorkTimestamp = normalizedPendingMessageCount > 0
        ? normalizedLatestPendingWorkTimestamp === undefined
          ? existing.latestPendingWorkTimestamp
          : normalizedLatestPendingWorkTimestamp
        : null
      const nextOptimisticWorkRequestPending =
        normalizedPendingMessageCount > 0 ? false : existing.optimisticWorkRequestPending
      if (
        existing.pendingMessageCount === normalizedPendingMessageCount &&
        existing.latestPendingWorkTimestamp === nextLatestPendingWorkTimestamp &&
        existing.optimisticWorkRequestPending === nextOptimisticWorkRequestPending
      ) {
        return state
      }

      if (existing.optimisticWorkRequestPending && !nextOptimisticWorkRequestPending) {
        clearOptimisticWorkRequestTimeout(normalizedSessionId)
      }

      const next = {
        ...existing,
        pendingMessageCount: normalizedPendingMessageCount,
        latestPendingWorkTimestamp: nextLatestPendingWorkTimestamp,
        optimisticWorkRequestPending: nextOptimisticWorkRequestPending,
      }

      return {
        entries: {
          ...state.entries,
          [normalizedSessionId]: next,
        },
        ...buildEntriesRevisionPatch({
          state,
          changedEntries: [{ previous: existing, next }],
        }),
      }
    })
  },

  applyPersistedEntries: (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return

    set((state) => {
      let entriesChanged = false
      let viewedEventIdsChanged = false
      const nextEntries: Record<string, ThreadRuntimeEntry> = { ...state.entries }
      const nextViewedEventIds: Record<string, number> = { ...state.viewedEventIds }
      const changedEntries: Array<{ previous: ThreadRuntimeEntry; next: ThreadRuntimeEntry }> = []

      for (const row of rows) {
        const sessionId = typeof row.sessionId === "string" ? row.sessionId.trim() : ""
        if (!sessionId) continue

        const persistedViewedEventId = normalizeViewedEventId(row.lastViewedEventId)
        const currentStoredViewedEventId = getStoredViewedEventId({
          viewedEventIds: nextViewedEventIds,
          sessionId,
        })
        const nextStoredViewedEventId = Math.max(currentStoredViewedEventId, persistedViewedEventId)

        if (nextStoredViewedEventId !== currentStoredViewedEventId) {
          nextViewedEventIds[sessionId] = nextStoredViewedEventId
          viewedEventIdsChanged = true
        }

        const existing = nextEntries[sessionId] ?? createEntry({
          sessionId,
          initialViewedEventId: nextStoredViewedEventId,
        })
        const persistedActivityEventId = normalizeEventId(row.lastActivityEventId)
        const persistedFinishedEventId = normalizeEventId(row.lastFinishedEventId)
        const persistedFinishedAt = normalizeTimestamp(row.lastFinishedAt)
        const persistedFinishReason = normalizeFinishReason(row.lastFinishReason)
        const persistedRawTailId = normalizeEventId(row.rawTailId)
        const persistedTerminalRawCursor = normalizeEventId(row.terminalRawCursor)
        const persistedTerminalRawCursorObservedAt = normalizeTimestamp(row.terminalRawCursorObservedAt)

        const nextActivityEventId = mergeNonRegressingEventId(
          existing.lastActivityEventId,
          persistedActivityEventId,
        )
        const nextFinishedEventId = mergeNonRegressingEventId(
          existing.lastFinishedEventId,
          persistedFinishedEventId,
        )
        const nextFinishedAt = mergeNonRegressingTimestamp(
          existing.lastFinishedAt,
          persistedFinishedAt,
        )
        const nextFinishReason = mergeFinishReason(
          existing.lastFinishReason,
          existing.lastFinishedEventId,
          persistedFinishReason,
          persistedFinishedEventId,
        )
        const nextRawTailId = mergeNonRegressingEventId(existing.rawTailId, persistedRawTailId)
        const nextTerminalRawCursor = persistedTerminalRawCursor ?? existing.terminalRawCursor
        const nextTerminalRawCursorObservedAt = mergeNonRegressingTimestamp(
          existing.terminalRawCursorObservedAt,
          persistedTerminalRawCursorObservedAt,
        )

        const next: ThreadRuntimeEntry = {
          ...existing,
          state:
            persistedActivityEventId !== null &&
            (existing.lastActivityEventId === null || persistedActivityEventId >= existing.lastActivityEventId)
              ? normalizeRuntimeState(row.state)
              : existing.state,
          lastActivityEventId: nextActivityEventId,
          lastFinishedEventId: nextFinishedEventId,
          lastFinishedAt: nextFinishedAt,
          lastFinishReason: nextFinishReason,
          rawTailId: nextRawTailId,
          terminalRawCursor: nextTerminalRawCursor,
          terminalRawCursorObservedAt: nextTerminalRawCursorObservedAt,
          lastViewedEventId: Math.max(existing.lastViewedEventId, nextStoredViewedEventId),
        }

        if (
          next.state !== existing.state ||
          next.lastActivityEventId !== existing.lastActivityEventId ||
          next.lastFinishedEventId !== existing.lastFinishedEventId ||
          next.lastFinishedAt !== existing.lastFinishedAt ||
          next.lastFinishReason !== existing.lastFinishReason ||
          next.rawTailId !== existing.rawTailId ||
          next.terminalRawCursor !== existing.terminalRawCursor ||
          next.terminalRawCursorObservedAt !== existing.terminalRawCursorObservedAt ||
          next.lastViewedEventId !== existing.lastViewedEventId
        ) {
          entriesChanged = true
          nextEntries[sessionId] = next
          changedEntries.push({ previous: existing, next })
        }
      }

      if (!entriesChanged && !viewedEventIdsChanged) {
        return state
      }

      return {
        entries: nextEntries,
        viewedEventIds: nextViewedEventIds,
        ...buildEntriesRevisionPatch({
          state,
          changedEntries,
        }),
      }
    })
  },

  setOptimisticYoloStartPending: ({ sessionId, pending, timeoutMs }) => {
    const normalizedSessionId = typeof sessionId === "string" ? sessionId.trim() : ""
    if (!normalizedSessionId) return

    clearOptimisticYoloStartTimeout(normalizedSessionId)
    if (pending && typeof timeoutMs === "number" && Number.isFinite(timeoutMs) && timeoutMs > 0) {
      const timeoutHandle = setTimeout(() => {
        useThreadRuntime.getState().setOptimisticYoloStartPending({
          sessionId: normalizedSessionId,
          pending: false,
        })
      }, timeoutMs)
      optimisticYoloStartTimeouts.set(normalizedSessionId, timeoutHandle)
    }

    set((state) => {
      const existing = state.entries[normalizedSessionId] ?? createEntry({
        sessionId: normalizedSessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: normalizedSessionId,
        }),
      })
      if (existing.optimisticYoloStartPending === pending) return state

      return {
        entries: {
          ...state.entries,
          [normalizedSessionId]: {
            ...existing,
            optimisticYoloStartPending: pending,
          },
        },
      }
    })
  },

  setYoloFlowStep: ({ sessionId, step, timestamp }) => {
    const normalizedSessionId = typeof sessionId === "string" ? sessionId.trim() : ""
    if (!normalizedSessionId) return

    const normalizedStep: YoloFlowRuntimeStep =
      step === "quick_pr" ||
      step === "merge_pr" ||
      step === "archiving" ||
      step === "done" ||
      step === "error"
        ? step
        : "idle"

    set((state) => {
      const existing = state.entries[normalizedSessionId] ?? createEntry({
        sessionId: normalizedSessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: normalizedSessionId,
        }),
      })
      const normalizedTimestamp = normalizeTimestamp(timestamp)
      if (normalizedTimestamp === null && existing.yoloFlowUpdatedAt !== null) {
        return state
      }
      if (
        normalizedTimestamp !== null &&
        existing.yoloFlowUpdatedAt !== null &&
        normalizedTimestamp < existing.yoloFlowUpdatedAt
      ) {
        return state
      }
      const nextOptimisticYoloStartPending =
        normalizedStep === "idle" ? existing.optimisticYoloStartPending : false
      if (
        existing.yoloFlowStep === normalizedStep &&
        existing.optimisticYoloStartPending === nextOptimisticYoloStartPending &&
        (normalizedTimestamp === null || normalizedTimestamp === existing.yoloFlowUpdatedAt)
      ) {
        return state
      }

      if (normalizedStep !== "idle") {
        clearOptimisticYoloStartTimeout(normalizedSessionId)
      }

      return {
        entries: {
          ...state.entries,
          [normalizedSessionId]: {
            ...existing,
            optimisticYoloStartPending: nextOptimisticYoloStartPending,
            yoloFlowStep: normalizedStep,
            yoloFlowUpdatedAt: normalizedTimestamp ?? existing.yoloFlowUpdatedAt,
          },
        },
      }
    })
  },

  setPrFlowOperation: ({ sessionId, kind, status, step, timestamp }) => {
    const normalizedSessionId = typeof sessionId === "string" ? sessionId.trim() : ""
    if (!normalizedSessionId) return false

    let applied = false

    set((state) => {
      const existing = state.entries[normalizedSessionId] ?? createEntry({
        sessionId: normalizedSessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId: normalizedSessionId,
        }),
      })

      const next = computeNextPrFlowOperationEntry({
        existing,
        input: {
          sessionId: normalizedSessionId,
          kind,
          status,
          step,
          timestamp,
        },
      })
      if (next === null) {
        return state
      }

      applied = true
      return {
        entries: {
          ...state.entries,
          [normalizedSessionId]: next,
        },
      }
    })

    return applied
  },

  setPrFlowOperations: (inputs) => {
    if (!Array.isArray(inputs) || inputs.length === 0) {
      return { appliedCount: 0, appliedInputs: [] }
    }

    let appliedCount = 0
    const appliedInputs: SetPrFlowOperationInput[] = []

    set((state) => {
      let changed = false
      const nextEntries: Record<string, ThreadRuntimeEntry> = { ...state.entries }

      for (const rawInput of inputs) {
        const normalizedSessionId = typeof rawInput.sessionId === "string" ? rawInput.sessionId.trim() : ""
        if (!normalizedSessionId) continue

        const existing = nextEntries[normalizedSessionId] ?? createEntry({
          sessionId: normalizedSessionId,
          initialViewedEventId: getStoredViewedEventId({
            viewedEventIds: state.viewedEventIds,
            sessionId: normalizedSessionId,
          }),
        })
        const next = computeNextPrFlowOperationEntry({
          existing,
          input: {
            ...rawInput,
            sessionId: normalizedSessionId,
          },
        })
        if (next === null) continue

        nextEntries[normalizedSessionId] = next
        appliedCount += 1
        appliedInputs.push({
          ...rawInput,
          sessionId: normalizedSessionId,
        })
        changed = true
      }

      return changed ? { entries: nextEntries } : state
    })

    return { appliedCount, appliedInputs }
  },

  markViewed: (sessionId) => {
    const existing = get().entries[sessionId]
    if (!existing) return null

    const target = Math.max(
      existing.lastViewedEventId,
      existing.lastActivityEventId ?? 0,
      existing.lastFinishedEventId ?? 0,
    )

    return get().markViewedByEventId(sessionId, target)
  },

  markViewedByEventId: (sessionId, viewedEventId) => {
    const normalizedViewedEventId = normalizeViewedEventId(viewedEventId)
    if (normalizedViewedEventId <= 0) return null

    let appliedViewedEventId: number | null = null
    set((state) => {
      const existing = state.entries[sessionId] ?? createEntry({
        sessionId,
        initialViewedEventId: getStoredViewedEventId({
          viewedEventIds: state.viewedEventIds,
          sessionId,
        }),
      })
      const storedViewedEventId = getStoredViewedEventId({
        viewedEventIds: state.viewedEventIds,
        sessionId,
      })
      const target = Math.max(existing.lastViewedEventId, storedViewedEventId, normalizedViewedEventId)
      if (target === existing.lastViewedEventId && target === storedViewedEventId) return state

      appliedViewedEventId = target

      const next = {
        ...existing,
        lastViewedEventId: target,
      }

      return {
        entries: {
          ...state.entries,
          [sessionId]: next,
        },
        viewedEventIds: {
          ...state.viewedEventIds,
          [sessionId]: target,
        },
        ...buildEntriesRevisionPatch({
          state,
          changedEntries: [{ previous: existing, next }],
        }),
      }
    })

    if (appliedViewedEventId !== null) {
      persistThreadRuntimeViewedEntry({
        scopeKey: threadRuntimeViewedStatePersistenceScopeKey,
        sessionId,
        viewedEventId: appliedViewedEventId,
      })
    }

    return appliedViewedEventId
  },

  pruneToSessions: (sessionIds) => {
    const keep = new Set(sessionIds)
    set((state) => {
      let changed = false
      const next: Record<string, ThreadRuntimeEntry> = {}
      for (const [sessionId, entry] of Object.entries(state.entries)) {
        if (!keep.has(sessionId)) {
          clearOptimisticWorkRequestTimeout(sessionId)
          clearOptimisticYoloStartTimeout(sessionId)
          changed = true
          continue
        }
        next[sessionId] = entry
      }
      return changed
        ? {
            entries: next,
            ...buildEntriesRemovalRevisionPatch({ state }),
          }
        : state
    })
  },

  getLastActivityEventId: (sessionId) => {
    const entry = get().entries[sessionId]
    return entry?.lastActivityEventId ?? null
  },
}))
